import { AnimatePresence, motion } from 'framer-motion';
import { X, Receipt, Target, CalendarDays, AlertTriangle, Users, ArrowRightLeft, CreditCard, CheckCircle2 } from 'lucide-react';

interface PolitiqueFacturationClientsRevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="bg-white/5 rounded-xl p-5 border border-emerald-500/20">
      <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
        <Icon className="w-5 h-5 text-emerald-300" />
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function PolitiqueFacturationClientsRevisionModal({ isOpen, onClose }: PolitiqueFacturationClientsRevisionModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 8 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 shadow-2xl"
        >
          <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-emerald-500/20 px-6 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="uppercase tracking-[0.25em] text-emerald-300 text-xs mb-1">Contenu en révision</p>
              <h2 className="text-2xl font-bold text-white">Politique de facturation clients et application de crédits</h2>
              <p className="text-gray-400 mt-1 text-sm max-w-3xl">
                Proposition de politique interne BZ pour standardiser la cadence de facturation, les exceptions et l'arrimage avec les crédits clients.
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <SectionCard title="Objectifs" icon={Target}>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>Centraliser et standardiser la facturation clients.</li>
                  <li>Centraliser et standardiser l'application de crédits clients.</li>
                  <li>Minimiser les exceptions et la micro-gestion.</li>
                  <li>Améliorer la communication entre les équipes.</li>
                </ul>
              </SectionCard>

              <SectionCard title="Champ d'application" icon={Users}>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Cette politique vise l'ensemble des employés internes de BZ inc. Elle reste en révision tant que les responsables et les règles d'approbation ne sont pas confirmés.
                </p>
              </SectionCard>
            </div>

            <SectionCard title="Cadence de facturation" icon={CalendarDays}>
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left py-3 px-4 text-emerald-200 font-semibold">Moment</th>
                      <th className="text-left py-3 px-4 text-emerald-200 font-semibold">Application</th>
                      <th className="text-left py-3 px-4 text-emerald-200 font-semibold">Principe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-white/10">
                      <td className="py-3 px-4 text-gray-300">1er du mois</td>
                      <td className="py-3 px-4 text-gray-300">Contrats BZ</td>
                      <td className="py-3 px-4 text-gray-300">Factures préparées, vérifiées et envoyées aux clients.</td>
                    </tr>
                    <tr className="border-t border-white/10">
                      <td className="py-3 px-4 text-gray-300">Fin du mois</td>
                      <td className="py-3 px-4 text-gray-300">Tickets</td>
                      <td className="py-3 px-4 text-gray-300">Factures liées aux tickets préparées, vérifiées et envoyées.</td>
                    </tr>
                    <tr className="border-t border-white/10">
                      <td className="py-3 px-4 text-gray-300">À tout moment</td>
                      <td className="py-3 px-4 text-gray-300">Matériel</td>
                      <td className="py-3 px-4 text-gray-300">Matériel informatique ou autre facturé lors de la livraison.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <div className="grid md:grid-cols-2 gap-4">
              <SectionCard title="Règle d'exception" icon={AlertTriangle}>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Toute demande client ou tout changement pouvant modifier le mécanisme de facturation doit obtenir au préalable l'autorisation du directeur ou de la directrice de département et celle de la direction.
                </p>
              </SectionCard>

              <SectionCard title="Crédits clients à clarifier" icon={CreditCard}>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>Qui initie une demande de crédit client.</li>
                  <li>Qui approuve selon le montant ou le contexte.</li>
                  <li>Comment le crédit est documenté dans les systèmes.</li>
                  <li>Comment la règle s'arrime avec les cartes Recevables et Demande de Crédits.</li>
                </ul>
              </SectionCard>
            </div>

            <SectionCard title="Promotion PRIsME" icon={ArrowRightLeft}>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                  <div className="font-semibold text-white mb-1">Destination suggérée</div>
                  <p className="text-gray-300">Administration & RH</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                  <div className="font-semibold text-white mb-1">Liens PRIsME</div>
                  <p className="text-gray-300">Gestion des Recevables, Demande de Crédits, RACI Admin/RH</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                  <div className="font-semibold text-white mb-1">Avant approbation</div>
                  <p className="text-gray-300">Responsables, seuils, date d'entrée en vigueur et cadence de révision.</p>
                </div>
              </div>
            </SectionCard>

            <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5" />
              <p className="text-sm text-emerald-100 leading-relaxed">
                Cette card conserve le contenu reçu tout en le gardant au statut éditorial approprié. Elle ne remplace pas encore la politique officielle ni les cartes Admin/RH existantes.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
